<script setup lang="ts">
useSeoMeta({
  title: 'Baghera Advisory - Conseil stratégique au service des dirigeants',
  description: "Conseil stratégique au service des dirigeants. Réflexions stratégiques, optimisation de l'organisation et mise en œuvre des leviers de performance.",
  ogTitle: 'Baghera Advisory - Conseil stratégique au service des dirigeants',
  ogDescription: "Conseil stratégique au service des dirigeants. Réflexions stratégiques, optimisation de l'organisation et mise en œuvre des leviers de performance.",
  ogImage: 'https://baghera-advisory.fr/img/desktop/panther_1440x532.webp',
  ogImageWidth: 1440,
  ogImageHeight: 532,
  ogType: 'website',
  ogLocale: 'fr_FR',
  ogSiteName: 'Baghera Advisory',
  ogUrl: 'https://baghera-advisory.fr/',
  twitterCard: 'summary_large_image',
  twitterTitle: 'Baghera Advisory - Conseil stratégique au service des dirigeants',
  twitterDescription: "Conseil stratégique au service des dirigeants. Réflexions stratégiques, optimisation de l'organisation et mise en œuvre des leviers de performance.",
  twitterImage: 'https://baghera-advisory.fr/img/desktop/panther_1440x532.webp',
})

// Add structured data for LocalBusiness and Person
useStructuredData()
usePersonSchema()
</script>

<template>
    <div class="page-index">
        <Header />
        <main class="page-index__main">
            <section class="section__intro">
                <div class="section__intro-logo">
                    <img src="~/assets/svg/logo-baghera.svg" alt="Baghera Advisory Logo" />
                </div>
                <div class="section__intro-resume">
                    <h1 class="section-title">
                        <span class="section-subtitle">Baghera Advisory</span> 
                        <br />Conseil stratégique au 
                        <br />service des dirigeants
                    </h1>
                    <p class="section-content">Nous accompagnons les équipes dirigeantes dans leurs
                        réflexions stratégiques, l'optimisation de leurs organisations et la mise en œuvre des leviers
                        de performance.</p>
                    <div class="section__intro-actions">
                        <ButtonAnchor to="/domaine-de-competences">Découvrez nos compétences</ButtonAnchor>
                    </div>
                </div>
            </section>

            <section class="section__convictions">
                <h2 class="section__convictions-title">Nos convictions : créer de la valeur durable</h2>
                <div class="section__convictions-items-container">
                    <div class="section__convictions-item">
                        <img src="/img/ecouter.svg" alt="Écouter" aria-hidden="true" />
                        <h3>écouter</h3>
                        <p>
                            Comprendre vos enjeux<br />
                            et vos aspirations
                        </p>
                    </div>
                    <div class="section__convictions-item">
                        <img src="/img/analyser.svg" alt="Analyser" aria-hidden="true" />
                        <h3>analyser</h3>
                        <p>
                            Diagnostiquer avec<br />
                            précision les défis et les<br />
                            opportunités
                        </p>
                    </div>
                    <div class="section__convictions-item">
                        <img src="/img/reinventer.svg" alt="Se réinventer" aria-hidden="true" />
                        <h3>se réinventer</h3>
                        <p>
                            Identifier et actionner les<br />
                            transformations<br />
                            nécessaires
                        </p>
                    </div>
                </div>

            </section>

            <section id="apropos" class="section__apropos">
                <div class="section__apropos-resume gap-4">
                    <h2 class="section-title">
                        À propos de <br />Grégory Sachnine
                    </h2>
                    <p class="section-content">Fort de 30 ans d’expérience en direction financière et direction générale dans des groupes industriels, Grégory Sachnine apporte une expertise éprouvée et une analyse pertinente des enjeux stratégiques des entreprises.
                        Son approche pragmatique et son écoute active font de lui un partenaire clé pour relever vos défis stratégiques, opérationnels et organisationnels.</p>
                    <ButtonAnchor to="/presentation-corporate-baghera-advisory-01-2026.pdf" blank class="mb-2">Téléchargez la plaquette</ButtonAnchor>
                    <ButtonAnchor :blank="true" />                    
                </div>
                <div class="section__apropos-right">
                    <picture class="picture" aria-hidden="true">
                        <source 
                            media="(min-width: 1024px)" 
                            type="image/webp"
                            srcset="/img/desktop/leaves_524x558.webp 1x, /img/desktop/leaves_1048x1116.webp 2x" 
                            width="1440" 
                            height="522" 
                        />
                        <source 
                            type="image/webp"
                            srcset="/img/mobile/leaves_524x558.webp 1x, /img/mobile/leaves_1048x1116.webp 2x" 
                            width="480"
                            height="532"
                        />
                        <img class="picture" src="/img/mobile/leaves_524x558.png" alt="" />
                    </picture>
                </div>
            </section>

            <Footer />
        </main>
    </div>
</template>

<style scoped lang="postcss">
.page-index {
    @apply flex flex-col items-center w-full
    laptop:max-w-[1440px] laptop:mx-auto;

    &__main {
        @apply relative mt-[532px] z-10;
    }
}
.section {
            
    &-title {
        @apply text-secondary text-3xl font-light mb-8;
    }

    &-subtitle {
        @apply text-secondary font-light text-xl;
    }
    
    &-content {
        @apply text-primary text-lg mb-10;
    }

    &__intro {
        @apply flex flex-col w-full laptop:flex-row;

        &-logo {
            @apply flex justify-center items-center bg-primary w-full p-36
            laptop:p-32 laptop:w-1/2;

            img {
                @apply w-[38.125vw] max-w-[223px];
            }
        }

        &-resume {
            @apply flex justify-center items-start flex-col bg-tertiary py-20 px-8
            laptop:px-28;
        }

        &-actions {
            @apply flex flex-col gap-4 laptop:flex-row laptop:gap-6;
        }
    }

    .nav-link {
        @apply inline-flex items-center justify-center gap-2 px-6 py-3
        bg-primary text-tertiary rounded-4xl
        transition-colors duration-300
        hover:bg-secondary hover:text-white
        focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2;
    }

    &__convictions {
        @apply flex flex-col items-center bg-secondary py-20 px-10;
        
        &-title {
            @apply text-tertiary text-4xl mb-8 text-center font-light;
        }

        &-resume {
            @apply text-primary text-lg mb-6 text-center;
        }

        &-items-container {
            @apply flex flex-col items-center gap-6
            laptop:flex-row laptop:gap-6 laptop:items-start;
        }

        &-item {
            @apply flex flex-col items-center gap-1 p-8;
            img {
                @apply w-[59px] h-[66px];
            }
            h3 {
                @apply text-tertiary text-2xl font-medium uppercase mb-3;
            }
            p {
                @apply text-primary text-lg text-center;
            }

            &:nth-child(2) {
                img {
                    @apply w-[102px] h-[65px];
                }
            }

            &:nth-child(3) {
                img {
                    @apply w-[120px] h-[65px];
                }
            }
        }
    }

    &__apropos {
        @apply flex flex-col w-full laptop:flex-row;

        &-resume {
            @apply flex justify-center items-start flex-col bg-tertiary py-20 px-8 w-full
            laptop:px-28 laptop:w-2/3;
        }

        &-right {
            @apply w-full laptop:w-1/3;
            .picture {
                @apply w-full h-full object-cover;
            }
        }
    }
}
</style>
